import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, map, of, startWith } from 'rxjs';
import { Calendar } from 'src/app/models/centrex/calendar';
import { RingStates } from 'src/app/models/centrex/enums/ring-states';
import { RuleTargetTypes } from 'src/app/models/centrex/enums/rule-target-types';
import { Extension } from 'src/app/models/centrex/extension';
import { Rule } from 'src/app/models/centrex/rule';
import { Service } from 'src/app/models/centrex/service';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { CalendarService } from 'src/app/services/centrex/calendar.service';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { RuleService } from 'src/app/services/centrex/rule.service';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { ExtensionDisplayPipe as ExtensionDisplayPipe_1 } from '../../../pipes/extension-display.pipe';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatLabel, MatFormField } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-global-rule-form',
    templateUrl: './global-rule-form.component.html',
    styleUrls: ['./global-rule-form.component.scss'],
    imports: [
        FaIconComponent,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        MatLabel,
        MatCheckbox,
        MatFormField,
        MatSelect,
        MatOption,
        MatInput,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatButton,
        RouterLink,
        AsyncPipe,
        TranslateModule,
        ExtensionDisplayPipe_1,
    ]
})
export class GlobalRuleFormComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    globalRuleInformations: Rule;
    globalRuleForm: UntypedFormGroup;
    isCreation: boolean;
    calendars: Calendar[] = [];

    targetTypes = Object.keys(RuleTargetTypes);
    ruleTargetTypes = RuleTargetTypes;
    ringStates = Object.keys(RingStates);

    extensionsList: Extension[] = [];
    servicesList: Service[] = [];

    extensionTargetOptions: Observable<Extension[]>;
    selectedTargetExtension: Extension;
    serviceTargetOptions: Observable<Service[]>;
    selectedTargetService: Service;

    private previousInstance: Instance;

    constructor(
        private route: ActivatedRoute,
        private ruleService: RuleService,
        private extensionService: ExtensionService,
        private serviceService: ServiceService,
        private calendarService: CalendarService,
        private organizationsService: OrganizationsService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.createForm({} as Rule);

        this.previousInstance = this.instanceService.instance();

        effect(() => {
            if (this.instanceService.instance() && this.previousInstance.id !== this.instanceService.instance().id) {
                this.router.navigate(['/globalrules']);
            }
        });
    }

    ngOnInit(): void {
        let ruleId: string;

        // Récupère l'identifiant de la règle
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            ruleId = params['id'];
        });

        this.loadDatas(ruleId);
    }

    loadDatas(ruleId: string) {
        this.spinner.show();

        if (ruleId) {
            this.isCreation = false;

            forkJoin([this.ruleService.getGlobalRule(ruleId), this.calendarService.getCalendars(), this.extensionService.getExtensions(), this.serviceService.getServices()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([globalRule, calendars, extensions, services]) => {
                        this.globalRuleInformations = globalRule;
                        this.calendars = calendars;
                        this.extensionsList = extensions;
                        this.servicesList = services;

                        if (globalRule.targetType === RuleTargetTypes.extension) {
                            this.selectedTargetExtension = extensions.find((a) => a.id === globalRule.target);
                        } else if (globalRule.targetType === RuleTargetTypes.service) {
                            this.selectedTargetService = services.find((a) => a.name === globalRule.target);
                        }

                        // Créé le formulaire
                        this.createForm(this.globalRuleInformations);
                    },
                    error: () => {
                        this.createForm(this.globalRuleInformations);
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;
            this.globalRuleInformations = new Rule();
            this.globalRuleInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';
            this.globalRuleInformations.enabled = true;

            forkJoin([this.calendarService.getCalendars(), this.extensionService.getExtensions(), this.serviceService.getServices()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([calendars, extensions, services]) => {
                        this.calendars = calendars;
                        this.extensionsList = extensions;
                        this.servicesList = services;

                        // Créé le formulaire
                        this.createForm(this.globalRuleInformations);
                    },
                    error: () => {
                        this.createForm(this.globalRuleInformations);
                        this.spinner.hide();
                    },
                });
        }
    }

    createForm(rule: Rule) {
        this.globalRuleForm = this.formbuilder.group({
            id: [rule.id],
            organizationId: [rule.organizationId],
            enabled: [rule.enabled],
            ringState: [rule.ringState],
            ringSeconds: [rule.ringSeconds],
            targetType: [rule.targetType],
            target: [rule.target],
            calendarId: [rule.calendarId],
            extensionTarget: [this.selectedTargetExtension, [this.autocompleteExtensionValidator()]],
            serviceTarget: [this.selectedTargetService, [this.autocompleteServiceValidator()]],
        });

        this.extensionTargetOptions = this.globalRuleForm.get('extensionTarget').valueChanges.pipe(
            startWith<string | Extension>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : value ? this.extensionDisplayPipe.transform(value) : null;
                return name ? this.extensionTargetSearch(name as string) : this.extensionsList.slice();
            })
        );

        this.serviceTargetOptions = this.globalRuleForm.get('serviceTarget').valueChanges.pipe(
            startWith<string | Service>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : null;
                return name ? this.serviceTargetSearch(name as string) : this.servicesList.slice();
            })
        );

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.ruleService
                .addGlobalRule(this.globalRuleInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('GlobalRule.SuccessMessageCreateGlobalRule'));

                        // Redirige sur la page des règles
                        this.router.navigate(['/globalrules']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('GlobalRule.ErrorMessageCreateGlobalRule'));

                        this.spinner.hide();
                    },
                });
        } else {
            this.ruleService
                .updateGlobalRule(this.globalRuleInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('GlobalRule.SuccessMessageUpdateGlobalRule'));

                        this.router.navigate(['/globalrules']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('GlobalRule.ErrorMessageUpdateGlobalRule'));

                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.globalRuleInformations.enabled = this.globalRuleForm.value.enabled;
        this.globalRuleInformations.ringState = this.globalRuleForm.value.ringState;
        this.globalRuleInformations.ringSeconds = this.globalRuleForm.value.ringSeconds;
        this.globalRuleInformations.targetType = this.globalRuleForm.value.targetType;
        this.globalRuleInformations.target = this.globalRuleForm.value.target;
        this.globalRuleInformations.calendarId = this.globalRuleForm.value.calendarId;
    }

    displayExtension = (extension: Extension): string => {
        return extension ? this.extensionDisplayPipe.transform(extension) : '';
    };

    displayService = (service: Service): string => {
        return service ? service.name : '';
    };

    extensionTargetChange(event: MatAutocompleteSelectedEvent) {
        let extension = event.option.value as Extension;
        if (extension) {
            this.globalRuleForm.get('target').setValue(extension.id);
            this.selectedTargetExtension = extension;
        }
    }

    serviceTargetChange(event: MatAutocompleteSelectedEvent) {
        let service = event.option.value as Service;
        if (service) {
            this.globalRuleForm.get('target').setValue(service.name);
            this.selectedTargetService = service;
        }
    }

    targetTypeChange(targetType: string) {
        this.globalRuleForm.get('target').setValue('');
        this.globalRuleForm.get('extensionTarget').setValue('');
        this.globalRuleForm.get('serviceTarget').setValue('');
    }

    private extensionTargetSearch(value: string): Extension[] {
        const filterValue = value.toLowerCase();

        return this.extensionsList.filter((a) => this.extensionDisplayPipe.transform(a).toLowerCase().includes(filterValue));
    }

    private serviceTargetSearch(value: string): Service[] {
        const filterValue = value.toLowerCase();

        return this.servicesList.filter((a) => a.name.toLowerCase().includes(filterValue));
    }

    private autocompleteExtensionValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.extensionsList.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }

    private autocompleteServiceValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.servicesList.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }
}
